import { useCallback } from "react"
import { useMutation } from "urql"

import { type MutationLockResult, MutationLockResultStatus } from "@/entityLock/types"
import { toEntityLock } from "@/entityLock/utils"
import type { UpdateInvoiceInput, UpdateInvoiceMutation } from "@/graphql/codegen/graphql"
import { UpdateInvoiceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseUpdateInvoiceReturn {
  updateInvoice: (input: UpdateInvoiceInput) => Promise<MutationLockResult>
  isUpdating: boolean
}

export const useUpdateInvoice = (): UseUpdateInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<UpdateInvoiceMutation, { input: UpdateInvoiceInput }>(
    UpdateInvoiceDocument
  )

  const updateInvoice = useCallback(
    async (input: UpdateInvoiceInput): Promise<MutationLockResult> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.invoice.update

      if (output?.__typename === "EntityLockFailure") {
        const currentLock = toEntityLock(output.error.currentLock)!
        return {
          status: MutationLockResultStatus.LockConflict,
          currentLock,
          message: output.error.message,
        }
      }

      const hasError = error || output?.__typename === "UpdateInvoiceFailure"

      if (hasError) {
        const message = output?.__typename === "UpdateInvoiceFailure" ? output.error.message : error?.message
        notify({ type: NotificationType.Error, message })
        return { status: MutationLockResultStatus.Error }
      }

      notify({ type: NotificationType.Success, message: "Invoice updated" })
      return { status: MutationLockResultStatus.Success }
    },
    [executeMutation, notify]
  )

  return { updateInvoice, isUpdating: fetching }
}
